import { useCallback, useEffect, useRef, useState } from 'react';
import $ from 'jquery';
import { FORM_NAMES, VERIFY_MODAL_TABS } from '~/app/constants';

// Asset form pattern (e.g. A00001) — used to determine assetMode
const assetFormRegExp = /^[A]{1}[0-9]{5}/;

const SPIN_LOAD_MSG = 'Loading Form...';

/**
 * Manages form navigation state and actions for the FormViewer.
 *
 * Owns:
 *   - activeForm / activeFile — the currently rendered form
 *   - formStack — back-navigation history (last 5 forms)
 *   - interviewMode — true when the active form is a wizard/interview (name starts with 'Z')
 *   - assetMode — true when the active form is a depreciation asset form
 *   - initialSpecialFormLoad — one-time flag for special form routing on first load
 *
 * Does NOT own:
 *   - Field event binding (delegated via onFormRendered)
 *   - Group toggle logic (delegated via onToggleGroup)
 *   - Spinner state (delegated via onStartSpin)
 *   - Verify / bookmark post-load behavior (delegated via onLoadVerifyForm)
 *   - Any business features triggered by specific form names (delegated via callbacks)
 *
 * @param {object}  params
 * @param {React.MutableRefObject<import('../../../api/calcAPI').default>} params.calcAPIRef
 * @param {string}  params.initialForm        Initial form name from return profile
 * @param {string}  params.initialFile        Initial file name from return profile
 * @param {boolean} params.isWizard           Whether we are in guided wizard mode
 * @param {boolean} params.isBusiness         payload.is_business — drives a CDSI edge case
 * @param {number}  params.season             Tax season year — drives a payment trigger on USIN01
 * @param {boolean} params.isNew              Whether this is a brand-new return
 * @param {boolean} params.startInterview     Whether to auto-start interview mode on open
 * @param {*}       params.estimatorBlob      Estimator blob (truthy check only)
 * @param {boolean} params.wizardCompleted    Whether the wizard has been marked complete
 *
 * @param {React.MutableRefObject<HTMLElement>} params.currentElRef
 *   Ref to the currently focused DOM field element. Owned by the field-events layer;
 *   used here only to snapshot the field when pushing to formStack.
 *
 * @param {React.MutableRefObject<string>}  params.bookmarkFieldRef
 *   Ref to the current bookmark field ID. Read at form-load time to restore focus.
 *
 * @param {React.MutableRefObject<boolean>} params.verifyDirtyRef
 *   Ref to the verifyDirty flag. Read at form-load time to trigger verify re-focus.
 *
 * @param {function(): boolean} params.hasFLST
 *   Returns true when a filing status has been set on the return.
 *   Used to guard sidebar navigation.
 *
 * --- Callbacks ---
 * All callbacks are optional. The hook stores each in a ref internally so callers
 * never need to memoize them.
 *
 * @param {function(string, string, string): void} params.onFormRendered
 *   Called after HTML is injected and state is updated: (formName, fileName, subFrm).
 *   Use this to re-bind field events (bindFormEvents equivalent).
 *
 * @param {function(Element, number, Event): void} params.onToggleGroup
 *   Called for each group-toggle change event on the rendered form.
 *   Signature mirrors the original toggleGroup(el, groupNumber, event).
 *
 * @param {function(): void}         params.onInterviewStart       Start interview mode
 * @param {function(): void}         params.onRequireFilingStatus  Show "filing status required" dialog
 * @param {function(number): void}   params.onVerifyReturn         Open the error/reject verify modal
 * @param {function(boolean): void}  params.onToggleEventLog       Toggle the event log modal
 * @param {function(boolean): void}  params.onToggleTaxpass        Toggle the Taxpass message modal
 * @param {function(boolean): void}  params.onToggleK1Manager      Toggle the K1 manager modal
 * @param {function(string, string): void} params.onLoadVerifyForm
 *   Called after a form renders when verifyDirty is set: (formName, fileName).
 * @param {function(): void}         params.onResetVerify
 *   Clears verify-chase state. Called on a deliberate sidebar navigation so the
 *   verify-field chase does not issue a stale form_load against the new form.
 * @param {function(boolean): Promise<void>} params.onSendRemotePayment
 *   Called before loading USIN01 in season >= 2023.
 * @param {function(string): void}   params.onStartSpin            Show loading spinner with message
 * * @param {function(): void}         params.onStopSpin             Hide loading spinner
 * * @param {function(string): void}   params.onSignatureFormLoad
 *   Called when a signature special form (prin12/13/17/18) is activated via sidebar or
 *   initial load. Use to trigger the return_print_query that populates the Signatures view.
 * @param {function(): void}         params.onCloseChoiceList
 *   Called before navigating to any special form. Use to close an open choicelist dropdown.
 */
const useFormNavigation = ({
  calcAPIRef,
  initialForm,
  initialFile,
  isWizard,
  isBusiness,
  season,
  isNew,
  startInterview,
  estimatorBlob,
  wizardCompleted,
  currentElRef,
  bookmarkFieldRef,
  verifyDirtyRef,
  hasFLST,
  onFormRendered,
  onToggleGroup,
  onInterviewStart,
  onRequireFilingStatus,
  onVerifyReturn,
  onToggleEventLog,
  onToggleTaxpass,
  onToggleK1Manager,
  onLoadVerifyForm,
  onResetVerify,
  onSendRemotePayment,
  onStartSpin,
  onStopSpin,
  onSignatureFormLoad,
  onCloseChoiceList,
}) => {
  // --- State ---
  const [activeForm, setActiveForm] = useState(isWizard ? FORM_NAMES.EST : initialForm);
  const [activeFile, setActiveFile] = useState(initialFile);
  const [formStack, setFormStack] = useState([]);
  const [interviewMode, setInterviewMode] = useState(false);
  const [assetMode, setAssetMode] = useState(false);
  const [initialSpecialFormLoad, setInitialSpecialFormLoad] = useState(false);

  // Refs for synchronous reads inside callbacks (avoids stale closures)
  const activeFormRef = useRef(activeForm);
  const activeFileRef = useRef(activeFile);
  const formStackRef = useRef(formStack);
  const initialSpecialFormLoadRef = useRef(initialSpecialFormLoad);

  useEffect(() => {
    activeFormRef.current = activeForm;
  }, [activeForm]);
  useEffect(() => {
    activeFileRef.current = activeFile;
  }, [activeFile]);
  useEffect(() => {
    formStackRef.current = formStack;
  }, [formStack]);
  useEffect(() => {
    initialSpecialFormLoadRef.current = initialSpecialFormLoad;
  }, [initialSpecialFormLoad]);

  // Callback refs — callers never need to memoize these
  const onFormRenderedRef = useRef(onFormRendered);
  const onToggleGroupRef = useRef(onToggleGroup);
  const onInterviewStartRef = useRef(onInterviewStart);
  const onRequireFilingStatusRef = useRef(onRequireFilingStatus);
  const onVerifyReturnRef = useRef(onVerifyReturn);
  const onToggleEventLogRef = useRef(onToggleEventLog);
  const onToggleTaxpassRef = useRef(onToggleTaxpass);
  const onToggleK1ManagerRef = useRef(onToggleK1Manager);
  const onLoadVerifyFormRef = useRef(onLoadVerifyForm);
  const onResetVerifyRef = useRef(onResetVerify);
  const onSendRemotePaymentRef = useRef(onSendRemotePayment);
  const onStartSpinRef = useRef(onStartSpin);
  const onStopSpinRef = useRef(onStopSpin);
  const onSignatureFormLoadRef = useRef(onSignatureFormLoad);
  const onCloseChoiceListRef = useRef(onCloseChoiceList);

  useEffect(() => {
    onFormRenderedRef.current = onFormRendered;
  }, [onFormRendered]);
  useEffect(() => {
    onToggleGroupRef.current = onToggleGroup;
  }, [onToggleGroup]);
  useEffect(() => {
    onInterviewStartRef.current = onInterviewStart;
  }, [onInterviewStart]);
  useEffect(() => {
    onRequireFilingStatusRef.current = onRequireFilingStatus;
  }, [onRequireFilingStatus]);
  useEffect(() => {
    onVerifyReturnRef.current = onVerifyReturn;
  }, [onVerifyReturn]);
  useEffect(() => {
    onToggleEventLogRef.current = onToggleEventLog;
  }, [onToggleEventLog]);
  useEffect(() => {
    onToggleTaxpassRef.current = onToggleTaxpass;
  }, [onToggleTaxpass]);
  useEffect(() => {
    onToggleK1ManagerRef.current = onToggleK1Manager;
  }, [onToggleK1Manager]);
  useEffect(() => {
    onLoadVerifyFormRef.current = onLoadVerifyForm;
  }, [onLoadVerifyForm]);
  useEffect(() => {
    onResetVerifyRef.current = onResetVerify;
  }, [onResetVerify]);
  useEffect(() => {
    onSendRemotePaymentRef.current = onSendRemotePayment;
  }, [onSendRemotePayment]);
  useEffect(() => {
    onStartSpinRef.current = onStartSpin;
  }, [onStartSpin]);
  useEffect(() => {
    onStopSpinRef.current = onStopSpin;
  }, [onStopSpin]);
  useEffect(() => {
    onSignatureFormLoadRef.current = onSignatureFormLoad;
  }, [onSignatureFormLoad]);
  useEffect(() => {
    onCloseChoiceListRef.current = onCloseChoiceList;
  }, [onCloseChoiceList]);

  // ─────────────────────────────────────────────────────────────────────────────
  // goToField
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Scrolls and focuses the given field ID on the rendered form.
   * No-op in wizard mode (scrollIntoView breaks the wizard header).
   */
  const goToField = useCallback(
    (_form, field) => {
      if (!isWizard) {
        setTimeout(() => {
          const el = document.getElementById(field);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.focus();
          }
        });
      }
    },
    [isWizard],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // scrollFormTo
  // ─────────────────────────────────────────────────────────────────────────────

  /** Scrolls the form container to a specific vertical position. */
  const scrollFormTo = useCallback(yCoord => {
    const formContainer = document.getElementsByClassName('formContainer')[0];
    if (formContainer) formContainer.scrollTop = yCoord;
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // updateFormStack / getPreviousForm
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Pushes the current form onto the navigation stack before loading a new form.
   * Keeps at most 5 entries (oldest is dropped).
   */
  const updateFormStack = useCallback(() => {
    const currentField = currentElRef?.current?.id ?? '';
    const stack = formStackRef.current;
    const lastEntry = stack[stack.length - 1];

    // Don't push a duplicate — same form already at top of stack
    if (lastEntry?.form === activeFormRef.current) return;

    const next = [
      ...stack,
      { form: activeFormRef.current, file: activeFileRef.current, field: currentField },
    ];
    const trimmed = next.length > 5 ? next.slice(1) : next;

    formStackRef.current = trimmed;
    setFormStack(trimmed);
  }, [currentElRef]);

  /**
   * Pops the most recent entry from the navigation stack and returns it.
   * Returns `undefined` when the stack is empty.
   *
   * @returns {{ form: string, file: string, field: string } | undefined}
   */
  const getPreviousForm = useCallback(() => {
    const stack = [...formStackRef.current];
    const previous = stack.pop();
    formStackRef.current = stack;
    setFormStack(stack);
    return previous;
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // setTaxForm  (handles the form_load response)
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Called with the raw HTML and metadata from a `form_load` socket response.
   * Injects the HTML into the DOM, updates navigation state, and notifies
   * the field-events layer to re-bind.
   *
   * Migration note: the jQuery HTML injection ($('#taxform').html(data)) is a
   * temporary pattern. The target architecture is dangerouslySetInnerHTML on a
   * ref'd container element.
   *
   * @param {string} data            Raw HTML from the calc server
   * @param {string} currentFormName form + occurrence + subFrm composite key
   * @param {string} currentFileName .xfm file name
   * @param {string} subFrm          Non-empty when this is a sub-form overlay
   */
  const setTaxForm = useCallback(
    (data, currentFormName, currentFileName, subFrm) => {
      onStartSpinRef.current?.(SPIN_LOAD_MSG);

      // Edge-case form name normalizations
      let curFrmName = currentFormName;
      if (isBusiness && currentFormName === 'CDSI00') {
        // Corp Info status page must redirect to 0001 for "print current form"
        curFrmName = '000100';
      } else if (curFrmName === '000a00') {
        // Entering CDS from a completed interview form
        curFrmName = curFrmName.toUpperCase();
      }

      setActiveForm(curFrmName);
      setActiveFile(currentFileName);
      setAssetMode(assetFormRegExp.test(curFrmName));

      // Inject server-rendered form HTML
      // TODO: replace with ref + dangerouslySetInnerHTML when jQuery is removed
      if (subFrm !== '') {
        $('#subform').html(data);
      } else {
        $('#taxform').html(data);
      }

      // Bind group-toggle change events (grp1–grp9 are exclusive-choice inputs)
      for (let i = 1; i <= 9; i++) {
        const grp = $(`#grp${i}`);
        if (grp[0]) {
          grp.change(e => {
            onToggleGroupRef.current?.(grp[0], i, e);
          });
        }
      }

      // Notify the field-events layer to re-bind all field listeners
      onFormRenderedRef.current?.(curFrmName, currentFileName, subFrm);

      // Post-load focus: bookmark takes priority over verify
      if (bookmarkFieldRef?.current && document.getElementById(bookmarkFieldRef.current)) {
        goToField(curFrmName, bookmarkFieldRef.current);
      } else if (verifyDirtyRef?.current) {
        onLoadVerifyFormRef.current?.(currentFormName, currentFileName);
      }
    },
    [isBusiness, goToField, bookmarkFieldRef, verifyDirtyRef],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // getTaxForm  (sends the ReturnFormLoad command)
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Requests a form from the calc server. The server responds with `form_load`,
   * which the caller should route to setTaxForm.
   *
   * @param {string}  name              Form occurrence string (e.g. '000A00', 'US104001')
   * @param {string}  [stmType]         Worksheet type ('wks', 'stm', 'ovl', or '')
   * @param {string}  [frmFile]         .xfm file name (or '' for default)
   * @param {string}  [field]           Field ID to focus after load (first 4 chars used)
   * @param {boolean} [shouldUpdateStack=true]  Set false when navigating back to avoid
   *                                            pushing the departing form twice
   */
  const getTaxForm = useCallback(
    async (name, stmType = '', frmFile = '', field = '', shouldUpdateStack = true) => {
      if (shouldUpdateStack) updateFormStack();

      if (initialSpecialFormLoadRef.current) {
        setInitialSpecialFormLoad(false);
      }

      onStartSpinRef.current?.(SPIN_LOAD_MSG);

      // Normalize generic CDS placeholder
      let resolvedName = name;
      if (resolvedName.slice(0, 4) === '0000') {
        resolvedName = FORM_NAMES.CDS;
      }

      // Interview mode is driven by the form name prefix 'Z'
      setInterviewMode(resolvedName.charAt(0).toUpperCase() === 'Z');

      // Payment-info form triggers a remote payment sync before load (season 2023+)
      if (resolvedName === 'USIN01' && season >= 2023) {
        await onSendRemotePaymentRef.current?.(false);
      }

      calcAPIRef.current?.ReturnFormLoad(resolvedName, stmType, frmFile, field.substring(0, 4));
    },
    [calcAPIRef, season, updateFormStack],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // doInitFormLoad  (routes the first form load after ReturnInit completes)
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Determines which form to load (or which modal to open) based on the initial
   * form stored in the return profile. Called once after the calc session is ready.
   */
  const doInitFormLoad = useCallback(() => {
    // In wizard mode wait until the wizard is marked complete before loading forms
    if (isWizard && !wizardCompleted) return;

    switch (activeFormRef.current) {
      case 'retn04':
      case 'retn09':
        getTaxForm(FORM_NAMES.CDS, '', '', '');
        onVerifyReturnRef.current?.(VERIFY_MODAL_TABS.REJECTS);
        break;

      case 'retn25':
        getTaxForm(FORM_NAMES.CDS, '', '', '');
        onToggleEventLogRef.current?.(true);
        break;

      case 'retn28':
        getTaxForm(FORM_NAMES.CDS, '', '', '');
        onToggleTaxpassRef.current?.(true);
        break;

      case 'retn19':
        navigateToNotesForm();
        break;

      case 'retn24':
        setActiveForm(activeFormRef.current);
        onStopSpinRef.current?.();
        break;
      case 'form12':
        // form12 needs the websocket roundtrip — the return_specialformload
        // reply triggers updateAddFormList('BA') in useSpecialFormLoad, which
        // populates the activity list the Asset Manager renders.
        getTaxForm(activeFormRef.current, '', activeFileRef.current, '');
        break;
      case 'prin12':
      case 'prin13':
      case 'prin17':
      case 'prin18':
        // React-rendered views — no websocket round-trip on initial load, so
        // nothing later will stop the return-open spinner. Stop it here (same
        // as retn24 above). Historically the spinner stopped as a side effect
        // of the auto return_print_query fired via onSignatureFormLoad, which
        // was removed when the Signatures page became status-only.
        setActiveForm(activeFormRef.current);
        onSignatureFormLoadRef.current?.(activeFormRef.current);
        onStopSpinRef.current?.();
        break;

      default: {
        if (isNew && startInterview && !estimatorBlob && !isBusiness) {
          onInterviewStartRef.current?.();
        } else {
          getTaxForm(activeFormRef.current, '', activeFileRef.current, '');

          // Mark initial special-form flag for forms that need special handling
          const form = activeFormRef.current;
          if (
            (form.includes('ret') &&
              form !== 'retn09' &&
              form !== 'retn04' &&
              form !== 'retn25' &&
              form !== 'retn28') ||
            form === 'form12' ||
            form === 'prin12' ||
            form === 'prin13' ||
            form === 'prin17' ||
            form === 'prin18'
          ) {
            setInitialSpecialFormLoad(true);
          } else if (isNew && form === FORM_NAMES.CDS) {
            calcAPIRef.current?.ReturnFormLoad('000A00', '', '', '');
          } else if (form === FORM_NAMES.CDS) {
            getTaxForm(FORM_NAMES.CDS, '', '', '');
          }
        }
        break;
      }
    }
  }, [
    isWizard,
    wizardCompleted,
    isNew,
    startInterview,
    estimatorBlob,
    isBusiness,
    calcAPIRef,
    getTaxForm,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // handleSidebarFormClick  (routes clicks from the form list sidebar)
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Handles a form selection from the sidebar navigation panel.
   * Expands/collapse icon clicks are intentionally ignored.
   *
   * @param {React.SyntheticEvent} event
   * @param {string} name     Form occurrence string
   * @param {string} stmcode  Worksheet/statement type
   * @param {string} file     .xfm file name
   */
  const handleSidebarFormClick = useCallback(
    (event, name, stmcode, file) => {
      // Clicks on the expand/collapse icon or malformed names are not navigations
      if (event.target.id.includes('Icon') || name.length < 6) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // Certain forms require a filing status before they can be accessed
      const flstNavForms = ['Z03001', 'UST101'];
      if (!hasFLST?.() && !flstNavForms.includes(name)) {
        onRequireFilingStatusRef.current?.();
        return;
      }

      onCloseChoiceListRef.current?.();

      switch (name) {
        case 'retn04':
        case 'retn09':
          onVerifyReturnRef.current?.(VERIFY_MODAL_TABS.REJECTS);
          break;
        case 'retn22':
          onToggleK1ManagerRef.current?.(true);
          break;
        case 'retn18':
        case 'retn19':
          // Set activeForm immediately so the render switch shows the component,
          // then still fire the websocket so the server sends back the latest data.
          updateFormStack();
          setActiveForm(name);
          getTaxForm(name, stmcode, file, '', false); // false = stack already updated above
          break;
        case 'retn24':
          updateFormStack();
          setActiveForm(name);
          break;
        case 'form12':
          // form12 needs the websocket roundtrip — the return_specialformload
          // reply triggers updateAddFormList('BA') in useSpecialFormLoad,
          // which populates the activity list the Asset Manager renders.
          getTaxForm(name, stmcode, file, '');
          break;
        case 'prin12':
        case 'prin13':
        case 'prin17':
        case 'prin18':
          // These views are React-rendered; set activeForm directly without websocket.
          // Notify the caller to send return_print_query.
          updateFormStack();
          setActiveForm(name);
          onSignatureFormLoadRef.current?.(name);
          break;
        case 'retn25':
          onToggleEventLogRef.current?.(true);
          break;
        case 'retn28':
          onToggleTaxpassRef.current?.(true);
          break;
        default:
          // A deliberate manual navigation — drop any pending verify-field
          // chase so it does not issue a stale form_load against this form.
          onResetVerifyRef.current?.();
          getTaxForm(name, stmcode, file, '');
      }
    },
    [hasFLST, getTaxForm, updateFormStack],
  );

  /**
   * Navigates directly to a React-rendered special form without sending a
   * websocket request. Use for forms whose content is owned entirely by a
   * React component (prin12-18, retn24, form12).
   */
  const navigateToSpecialForm = useCallback(
    name => {
      updateFormStack();
      onCloseChoiceListRef.current?.();
      setActiveForm(name);
      setAssetMode(false);
    },
    [updateFormStack],
  );

  /**
   * Navigates to the preparer Notes form (retn19).
   * Sets activeForm immediately so the React component renders right away,
   * then fires the websocket so the server sends back the latest notes via
   * special_form_load.
   *
   * When already on the Notes form (e.g. initial load where initialForm = 'retn19',
   * or server sends stat03 with the return already open on notes), skips navigation
   * but still stops the spinner — notes are already populated from return_init.
   */
  const navigateToNotesForm = useCallback(() => {
    if (activeFormRef.current === 'retn19') {
      // Already on the Notes form. Notes arrive from return_init, just clear the spinner.
      onStopSpinRef.current?.();
      return;
    }
    updateFormStack();
    setActiveForm('retn19');
    getTaxForm('retn19', '', '', '', false); // false = stack already updated above
  }, [getTaxForm, updateFormStack]);

  // ─────────────────────────────────────────────────────────────────────────────
  return {
    // State
    activeForm,
    activeFile,
    formStack,
    interviewMode,
    assetMode,
    initialSpecialFormLoad,
    setInitialSpecialFormLoad,
    // Actions
    getTaxForm,
    setTaxForm,
    goToField,
    scrollFormTo,
    updateFormStack,
    getPreviousForm,
    handleSidebarFormClick,
    doInitFormLoad,
    navigateToSpecialForm,
    navigateToNotesForm,
  };
};

export default useFormNavigation;
